/**
 * Module dependencies.
 */

import { TourData } from 'src/types/tour';
import { forwardRef, useEffect, useState } from 'react';
import { media } from 'src/styles/media';
import VideoPlayer from 'src/components/video-player';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = TourData['sections']['video'] & {
  isInView?: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: var(--color-grey-100);
  cursor: pointer;
  min-width: 100vw;
  padding-bottom: 56.5%;
  position: relative;

  ${media.min.md`
    height: 100vh;
    padding: 0;
    z-index: 1;
  `};
`;

/**
 * `VideoSection` component.
 */

const VideoSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [zIndex, setZIndex] = useState<number>(0);
  const isMobile = useBreakpoint('md', 'max');

  useEffect(() => {
    setIsPlaying((props?.isInView && hasInitialized) || false);
  }, [hasInitialized, props?.isInView]);

  return (
    <Wrapper onClick={() => setIsPlaying(!isPlaying)} ref={ref}>
      <VideoPlayer
        config={{
          youtube: {
            playerVars: { rel: 0, showinfo: 0 }
          }
        }}
        light={props?.cover}
        onClickPreview={() => {
          setZIndex(-1);
        }}
        onPlay={() => setHasInitialized(true)}
        playing={isPlaying}
        style={!isMobile && { zIndex }}
        url={props?.url}
        volume={0.3}
      />
    </Wrapper>
  );
});

VideoSection.displayName = 'VideoSection';

/**
 * Export `VideoSection` component.
 */

export default VideoSection;
